<template>
  <div class="japan-map-container">
    <!-- SVG 日本地図 -->
    <div ref="mapScrollContainer" class="svg-map-wrapper">
      <svg
        ref="svgMap"
        viewBox="0 0 1000 846"
        class="japan-svg-map"
        @mousemove="handleMouseMove"
      >
        <!-- SVGの内容を動的に読み込んで各pathにイベントとスタイルを適用 -->
        <g id="features">
          <path
            v-for="path in svgPaths"
            :key="path.id"
            :id="path.id"
            :d="path.d"
            :fill="getPrefectureFillColor(path.id)"
            :stroke="getPrefectureStroke(path.id)"
            :stroke-width="getPrefectureStrokeWidth(path.id)"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="prefecture-path"
            @mouseenter="handlePrefectureHover(path.id)"
            @mouseleave="handlePrefectureLeave"
            @click="handlePrefectureClick(path.id)"
          />
        </g>
      </svg>
    </div>

    <!-- ホバー時のポップアップ -->
    <div
      v-if="hoveredPrefectureId && shioriByPrefecture[hoveredPrefectureId]"
      class="prefecture-popup"
      :style="popupStyle"
    >
      <div class="popup-header">
        {{ getPrefectureName(hoveredPrefectureId) }}
      </div>
      <div class="popup-content">
        <div
          v-for="shiori in shioriByPrefecture[hoveredPrefectureId]"
          :key="shiori.SHIORI_NOV"
          class="popup-shiori-item"
          @click.stop="$emit('shiori-click', shiori.SHIORI_NOV)"
        >
          <div class="popup-shiori-title">{{ shiori.SUBJECT_NMV }}</div>
          <div class="popup-shiori-date">
            {{ formatDate(shiori.TRIP_FROM) }} 〜
            {{ formatDate(shiori.TRIP_TO) }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, nextTick, onMounted } from "vue";
import { getISOCode, getPrefectureName } from "@/data/prefectureMapping";
import { useDestinationMasterStore } from "@/store/destination-master-store.js";

export default {
  name: "JapanMapSVG",
  props: {
    shioriList: {
      type: Array,
      required: true,
    },
  },
  emits: ["shiori-click"],
  setup(props, { emit }) {
    const svgMap = ref(null);
    const mapScrollContainer = ref(null);
    const hoveredPrefectureId = ref(null);
    const mousePosition = ref({ x: 0, y: 0 });
    const svgPaths = ref([]);
    const destMasterStore = useDestinationMasterStore();

    // SVGからpathデータを抽出
    onMounted(async () => {
      await destMasterStore.fetchAll();
      await parseSVGPaths();
      await centerMapScroll();
    });

    const centerMapScroll = async () => {
      await nextTick();
      const container = mapScrollContainer.value;
      if (!container) return;
      container.scrollLeft = Math.max(
        0,
        (container.scrollWidth - container.clientWidth) / 2,
      );
    };

    const parseSVGPaths = async () => {
      try {
        // SVGファイルをfetchで取得
        const response = await fetch("/jp.svg");
        const svgText = await response.text();

        // SVGテキストからpath要素を抽出
        const parser = new DOMParser();
        const svgDoc = parser.parseFromString(svgText, "image/svg+xml");
        const paths = svgDoc.querySelectorAll('path[id^="JP"]');

        svgPaths.value = Array.from(paths).map((path) => ({
          id: path.getAttribute("id"),
          name: path.getAttribute("name"),
          d: path.getAttribute("d"),
        }));
      } catch (error) {
        console.error("SVGファイルの読み込みエラー:", error);
      }
    };

    // 都道府県IDごとにしおりをグループ化
    const shioriByPrefecture = computed(() => {
      const grouped = {};

      props.shioriList.forEach((shiori) => {
        try {
          if (shiori.DESTINATIONS) {
            const destinations = JSON.parse(shiori.DESTINATIONS);
            destinations.forEach((prefectureName) => {
              const isoCode = getISOCode(prefectureName);
              if (isoCode) {
                if (!grouped[isoCode]) {
                  grouped[isoCode] = [];
                }
                grouped[isoCode].push(shiori);
              }
            });
          }
        } catch (error) {
          console.error("渡航先データのパースエラー:", error);
        }
      });

      return grouped;
    });

    // 都道府県の塗りつぶし色を取得
    const getPrefectureFillColor = (prefectureId) => {
      const hasShiori =
        shioriByPrefecture.value[prefectureId] &&
        shioriByPrefecture.value[prefectureId].length > 0;
      const isHovered = hoveredPrefectureId.value === prefectureId;

      if (isHovered && hasShiori) {
        return "#C5DCB3"; // ホバー時の濃い紫
      } else if (hasShiori) {
        return "#B4CF9E"; // しおりあり: 紫
      } else if (isHovered) {
        return "#e2e8f0"; // しおりなし＋ホバー: 薄いグレー
      } else {
        return "#ffffff"; // デフォルト: 白
      }
    };

    // 都道府県の枠線色を取得
    const getPrefectureStroke = (prefectureId) => {
      const hasShiori =
        shioriByPrefecture.value[prefectureId] &&
        shioriByPrefecture.value[prefectureId].length > 0;
      const isHovered = hoveredPrefectureId.value === prefectureId;

      if (isHovered && hasShiori) {
        return "#C5DCB3"; // ホバー時の濃い紫
      } else {
        return "#cbd5e0"; // デフォルト: グレー
      }
    };

    // 都道府県の枠線幅を取得
    const getPrefectureStrokeWidth = (prefectureId) => {
      const isHovered = hoveredPrefectureId.value === prefectureId;
      return isHovered ? "2" : "0.5";
    };

    // マウスホバー処理
    const handlePrefectureHover = (prefectureId) => {
      hoveredPrefectureId.value = prefectureId;
    };

    // マウスリーブ処理
    const handlePrefectureLeave = () => {
      hoveredPrefectureId.value = null;
    };

    // マウス移動処理（ポップアップ位置更新）
    const handleMouseMove = (event) => {
      mousePosition.value = {
        x: event.clientX,
        y: event.clientY,
      };
    };

    // 都道府県クリック処理
    const handlePrefectureClick = (prefectureId) => {
      const shioriList = shioriByPrefecture.value[prefectureId];
      if (shioriList && shioriList.length > 0) {
        if (shioriList.length === 1) {
          // しおりが1つの場合、直接遷移
          emit("shiori-click", shioriList[0].SHIORI_NOV);
        }
        // 複数ある場合はポップアップから選択
      }
    };

    // ポップアップのスタイル（マウス位置に追従）
    const popupStyle = computed(() => ({
      left: `${mousePosition.value.x + 15}px`,
      top: `${mousePosition.value.y + 15}px`,
    }));

    // 日付フォーマット
    const formatDate = (dateString) => {
      if (!dateString) return "";
      const date = new Date(dateString);
      return `${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()}`;
    };

    return {
      svgMap,
      mapScrollContainer,
      svgPaths,
      hoveredPrefectureId,
      shioriByPrefecture,
      popupStyle,
      getPrefectureFillColor,
      getPrefectureStroke,
      getPrefectureStrokeWidth,
      handlePrefectureHover,
      handlePrefectureLeave,
      handleMouseMove,
      handlePrefectureClick,
      getPrefectureName,
      formatDate,
    };
  },
};
</script>

<style scoped>
.japan-map-container {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 0;
  background-color: #f7fafc;
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.svg-map-wrapper {
  width: 100%;
  height: 100%;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
}

.japan-svg-map {
  width: 100%;
  height: 100%;
  filter: drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1));
}

.prefecture-path {
  cursor: pointer;
  transition: all 0.2s ease;
}

.prefecture-path:hover {
  filter: brightness(0.95);
}

/* ポップアップスタイル */
.prefecture-popup {
  position: fixed;
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  padding: 0;
  z-index: 1000;
  min-width: 250px;
  max-width: 350px;
  pointer-events: none;
}

.popup-header {
  background: linear-gradient(135deg, #b4cf9e 0%, #c5dcb3 100%);
  color: white;
  padding: 0.75rem 1rem;
  font-weight: 700;
  font-size: 1rem;
  border-radius: 12px 12px 0 0;
}

.popup-content {
  padding: 0.5rem;
  max-height: 300px;
  overflow-y: auto;
}

.popup-shiori-item {
  padding: 0.75rem;
  border-radius: 8px;
  margin-bottom: 0.5rem;
  background: #f7fafc;
  transition: background 0.2s;
  pointer-events: auto;
  cursor: pointer;
}

.popup-shiori-item:hover {
  background: #e2e8f0;
}

.popup-shiori-item:last-child {
  margin-bottom: 0;
}

.popup-shiori-title {
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 0.25rem;
  font-size: 0.95rem;
}

.popup-shiori-date {
  font-size: 0.85rem;
  color: #718096;
}

@media (max-width: 640px) {
  .svg-map-wrapper {
    justify-content: flex-start;
    overflow-x: auto;
    overflow-y: hidden;
    padding: 6px;
    scrollbar-width: thin;
  }

  .japan-svg-map {
    min-width: 520px;
    flex: 0 0 520px;
  }
}
</style>
